import { useState } from "react";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import { KeyboardEvent } from "react";
import { createUseStyles } from "react-jss";
import { useAtom } from "jotai";
import { hapticTap } from "../helpers/haptics";
import { answeredState } from "../helpers/recoil";
import { HskEntry } from "../helpers/types";
import { useLongPress } from "../helpers/useLongPress";
import CardDetailPopover from "./CardDetailPopover";

const CHAR_SIZE = 20;

const useStyles = createUseStyles({
  tile: {
    position: "relative",
    aspectRatio: "1",
    minHeight: 64,
    borderRadius: 8,
    border: "1px solid #404754",
    backgroundColor: "#2c3139",
    cursor: "pointer",
    overflow: "hidden",
    touchAction: "manipulation",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    padding: 4,
    boxSizing: "border-box",
    transition: "border-color 0.35s ease, box-shadow 0.35s ease, background-color 0.35s ease"
  },
  tileCorrect: {
    backgroundColor: "#243530",
    borderColor: "#0F6E56",
    boxShadow: "0 0 0 1px rgba(15, 110, 86, 0.55)"
  },
  tileWrong: {
    backgroundColor: "#352b2b",
    borderColor: "#993C1D",
    boxShadow: "0 0 0 1px rgba(153, 60, 29, 0.55)"
  },
  char: {
    fontSize: CHAR_SIZE,
    fontWeight: 500,
    lineHeight: 1,
    whiteSpace: "nowrap"
  },
  charSimp: {
    color: "#F0997B"
  },
  charTrad: {
    color: "#85B7EB"
  },
  charSingle: {
    fontSize: CHAR_SIZE,
    fontWeight: 500,
    lineHeight: 1,
    color: "#F0997B"
  },
  badge: {
    position: "absolute",
    top: 2,
    right: 2,
    zIndex: 2,
    width: 14,
    height: 14,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "none",
    "& svg": {
      fontSize: 10
    }
  },
  badgeCorrect: {
    backgroundColor: "#E1F5EE",
    color: "#0F6E56"
  },
  badgeWrong: {
    backgroundColor: "#FAECE7",
    color: "#993C1D"
  }
});

const CharContent = ({ charSimp, charTrad }: { charSimp: string; charTrad: string }) => {
  const classes = useStyles();
  const simp = charSimp?.trim();
  const trad = charTrad?.trim();

  if (!simp && !trad) {
    return null;
  }

  const same = simp && trad && simp === trad;

  if (same || !trad) {
    return <span className={classes.charSingle}>{simp || trad}</span>;
  }

  if (!simp) {
    return <span className={`${classes.char} ${classes.charTrad}`}>{trad}</span>;
  }

  return (
    <>
      <span className={`${classes.char} ${classes.charSimp}`}>{simp}</span>
      <span className={`${classes.char} ${classes.charTrad}`}>{trad}</span>
    </>
  );
};

const CompactCard = ({
  card,
  answersByOrder,
  readOnly = false
}: {
  card: HskEntry;
  answersByOrder?: Record<number, boolean | null | undefined>;
  readOnly?: boolean;
}) => {
  const [answeredList, setAnsweredList] = useAtom(answeredState);
  const [detailOpen, setDetailOpen] = useState(false);
  const answer =
    answersByOrder !== undefined
      ? answersByOrder[card.order]
      : answeredList.find((a) => a.order === card.order)?.answer;
  const classes = useStyles();

  const cycleMark = () => {
    if (readOnly) {
      setDetailOpen(true);
      return;
    }
    hapticTap();
    setAnsweredList((prev) => {
      const current = prev.find((a) => a.order === card.order)?.answer;
      if (current === undefined) {
        return [...prev, { order: card.order, answer: true }];
      }
      if (current === true) {
        return prev.map((item) =>
          item.order === card.order ? { order: card.order, answer: false } : item
        );
      }
      return prev.filter((item) => item.order !== card.order);
    });
  };

  const pressHandlers = useLongPress(() => setDetailOpen(true), cycleMark);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      cycleMark();
    }
  };

  const markLabel =
    answer === true
      ? "marked known"
      : answer === false
        ? "marked incorrect"
        : "unmarked";

  return (
    <>
      <div
        className={`${classes.tile} ${answer === true ? classes.tileCorrect : ""} ${
          answer === false ? classes.tileWrong : ""
        }`}
        role="button"
        tabIndex={0}
        aria-label={
          readOnly
            ? `Character card ${card.order}, ${markLabel}. Tap for details.`
            : `Character card ${card.order}, ${markLabel}. Tap to cycle mark. Hold for details.`
        }
        onKeyDown={handleKeyDown}
        {...pressHandlers}>
        {answer === true && (
          <span className={`${classes.badge} ${classes.badgeCorrect}`} aria-hidden="true">
            <CheckIcon />
          </span>
        )}
        {answer === false && (
          <span className={`${classes.badge} ${classes.badgeWrong}`} aria-hidden="true">
            <ClearIcon />
          </span>
        )}
        <CharContent charSimp={card.charSimp} charTrad={card.charTrad} />
      </div>
      <CardDetailPopover card={card} open={detailOpen} onClose={() => setDetailOpen(false)} />
    </>
  );
};

export default CompactCard;
